import { Box, Spinner, Text, VStack } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Button from "@pathwright/ui/src/components/button/Button"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { exhaustiveGuard } from "@pathwright/ux/src/utils/typing"
import PropTypes from "prop-types"
import { useCohortsStatusQuery } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import useUpgradePrompt from "../../subscription/manage/useUpgradePrompt"

const tPrefix = "cohort.gate.included_cohorts"

export function useCohortsUpgradePrompt(resourceId: number) {
  const cohortsStatusQuery = useCohortsStatusQuery({
    variables: { resourceId },
    fetchPolicy: "cache-and-network",
    skip: !resourceId
  })

  const inProgressCount =
    cohortsStatusQuery.data?.resource?.cohorts_status_meta?.in_progress || 0

  return useUpgradePrompt({
    key: "cohorts",
    totalUsed: inProgressCount
  })
}

type CohortsUpgradeGate = {
  icon: IconName
  heading: string
  body: string | JSX.Element
  primaryAction: LinkProps
  secondaryAction: LinkProps
}

export function useCohortsUpgradeGate(
  resourceId: number
): CohortsUpgradeGate | null | false {
  const { prompt, totalIncluded } = useCohortsUpgradePrompt(resourceId)
  const { school } = usePathwrightContext()
  const { t } = useTranslate()
  const cohortsStatusQuery = useCohortsStatusQuery({
    variables: { resourceId },
    fetchPolicy: "cache-only",
    skip: !resourceId
  })

  // A false return indicates the gate deps are still loading.
  if (!cohortsStatusQuery.data) {
    return false
  }

  switch (prompt) {
    case "activate": {
      const activateTPrefix = `${tPrefix}.activate`

      return {
        icon: "rocket",
        heading: t(`${activateTPrefix}.Upgrade to Cohorts`),
        body: t(
          `${activateTPrefix}.Choose a plan to support one or more cohorts per course while keeping everything in sync.`
        ),
        primaryAction: {
          children: t(`${activateTPrefix}.View Plans`),
          to: "/pricing/plan/"
        },
        secondaryAction: {
          children: t(`${activateTPrefix}.Learn more...`),
          href: "https://help.pathwright.com/en/articles/5180927-how-to-pick-the-right-plan-based-on-cohorts-you-plan-to-offer",
          target: "_blank"
        }
      }
    }
    case "upgrade":
      // ensure that the resource is either not licensed or is
      // licensed from this same school
      if (
        !cohortsStatusQuery.data?.resource?.licensed_from_school_id ||
        cohortsStatusQuery.data?.resource?.licensed_from_school_id === school.id
      ) {
        const upgradeTPrefix = `${tPrefix}.upgrade`
        // prompt upgrade
        return {
          icon: "rocket",
          heading: t(`${upgradeTPrefix}.Upgrade Cohorts`),
          body: (
            <>
              {t(
                `${upgradeTPrefix}.Your plan includes {{ count }} in-progress cohort per course`,
                {
                  defaultValue_plural: `${upgradeTPrefix}.Your plan includes {{ count }} in-progress cohorts per course`,
                  count: totalIncluded ?? undefined
                }
              )}
              .
              <br />
              <br />
              You can{" "}
              <Button
                styleType="text"
                href="https://help.pathwright.com/en/articles/5163383-mark-a-cohort-complete"
                target="_blank"
              >
                mark an existing cohort as complete
              </Button>{" "}
              to create another one that's in progress or upgrade your plan to
              include more in-progress Cohorts per course.
            </>
          ),
          primaryAction: {
            children: t(`${upgradeTPrefix}.Upgrade`),
            to: "/pricing/plan/"
          },
          secondaryAction: {
            children: t(`${upgradeTPrefix}.Learn more`),
            href: "https://help.pathwright.com/en/articles/5180927-how-to-pick-the-right-plan-based-on-cohorts-you-plan-to-offer",
            target: "_blank"
          }
        }
      }
      return null
    // NOTE: otherwise falling through to the NONE case
    case null:
      return null
    default:
      exhaustiveGuard(prompt)
  }
}

type CohortsUpgradeInlineGate = {
  icon: IconName
  heading: string
  body: string | JSX.Element
}

export type UseCohortsUpgradeInlineGateReturn =
  | CohortsUpgradeInlineGate
  | null
  | false

export function useCohortsUpgradeInlineGate(
  resourceId: number
): CohortsUpgradeInlineGate | null | false {
  const { prompt, totalIncluded } = useCohortsUpgradePrompt(resourceId)
  const { school } = usePathwrightContext()
  const { t, Trans } = useTranslate()

  const cohortsStatusQuery = useCohortsStatusQuery({
    variables: { resourceId },
    fetchPolicy: "cache-only",
    skip: !resourceId
  })

  const upgradeGate = useCohortsUpgradeGate(resourceId)

  // A false return indicates the gate deps are still loading.
  if (!upgradeGate) {
    return upgradeGate
  }

  if (cohortsStatusQuery.data?.resource?.copied_with_copy_config_id != null) {
    return null
  }

  switch (prompt) {
    case "activate": {
      const activateTPrefix = `${tPrefix}.activate`

      return {
        icon: upgradeGate.icon,
        heading: upgradeGate.heading,
        body: (
          <>
            <Trans
              i18nKey={`${activateTPrefix}.<a>Choose a plan</a> with {{count}}+ Cohorts per Path.`}
              components={{
                a: <Link {...upgradeGate.primaryAction} textDecor="underline" />
              }}
              count={(totalIncluded || 0) + 1}
            />{" "}
            <Link {...upgradeGate.secondaryAction} textDecor="underline">
              {t(`${activateTPrefix}.Learn more about multiple Cohorts.`)}
            </Link>
          </>
        )
      }
    }
    case "upgrade":
      // ensure that the resource is either not licensed or is
      // licensed from this same school
      if (
        !cohortsStatusQuery.data?.resource?.licensed_from_school_id ||
        cohortsStatusQuery.data?.resource?.licensed_from_school_id === school.id
      ) {
        const upgradeTPrefix = `${tPrefix}.upgrade`
        // prompt upgrade (not currently a prompt! Ask Paul for blank slate)
        return {
          icon: upgradeGate.icon,
          heading: upgradeGate.heading,
          body: (
            <Text>
              {t(
                `${upgradeTPrefix}.Your plan includes {{ count }} in-progress cohort per Path.`,
                {
                  defaultValue_plural: `${upgradeTPrefix}.Your plan includes {{ count }} in-progress cohorts per Path.`,
                  count: totalIncluded ?? undefined
                }
              )}
            </Text>
          )
        }
      }
      return null
    // NOTE: otherwise falling through to the NONE case
    case null:
      return null
    default:
      exhaustiveGuard(prompt)
  }
}

type UpgradeCohortsGateProps = {
  resourceId: number
  // Allow showing an overlay loading state (overlaying children) when the gate is being determined
  // instead of just a full loading state (without rendering children).
  overlayLoading?: boolean
  children: React.ReactNode
}

const UpgradeCohortsGate = ({
  resourceId,
  overlayLoading,
  children
}: UpgradeCohortsGateProps) => {
  // only show loading circle if querying for the resource on mount
  const gate = useCohortsUpgradeGate(resourceId)

  return gate === false && !!resourceId ? (
    overlayLoading ? (
      <Box pos="relative" w="full" h="full">
        <Box
          css={{
            "& .chakra-spinner": {
              display: "none"
            }
          }}
          w="full"
          h="full"
        >
          {children}
        </Box>
        <Box pos="absolute" inset={0} bg="whiteAlpha.300" />
        <VStack w="full" h="full" justifyContent="center">
          <Spinner />
        </VStack>
      </Box>
    ) : (
      <VStack w="full" h="full" minH="200px" justifyContent="center">
        <Spinner />
      </VStack>
    )
  ) : gate ? (
    <BlankSlate {...gate} stack />
  ) : (
    children
  )
}

UpgradeCohortsGate.displayName = "UpgradeCohortsGate"

UpgradeCohortsGate.propTypes = {
  resourceId: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired
}

export default UpgradeCohortsGate
